import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

// Local types for the Supabase-based implementation
interface DashboardMetrics {
  total_organizations: number
  new_organizations: number
  active_subscriptions: number
  trial_subscriptions: number
  churned_subscriptions: number
  suspended_organizations: number
  trial_expiring_soon: number
  total_mrr: number
  total_arr: number
  active_alerts: number
  period: string
  organizations_growth: number
  mrr_growth: number
  churn_rate: number
}

interface DashboardMetricsQuery {
  period?: string
  start_date?: string
  end_date?: string
}

type DashboardBillingStatus = 'none' | 'trial' | 'active' | 'past_due' | 'canceled' | 'suspended'
type SubscriptionRevenueRow = {
  subscription_plans: {
    price_monthly_cents: number | null
  } | null
}

function normalizeDashboardBillingStatus(status: string | null | undefined): DashboardBillingStatus {
  switch (status) {
    case 'trial':
    case 'active':
    case 'past_due':
    case 'suspended':
    case 'none':
      return status
    case 'canceled':
    case 'cancelled':
    case 'churned':
      return 'canceled'
    default:
      return 'none'
  }
}

function getDateRange(period: string): { start: Date; end: Date } {
  const end = new Date()
  const start = new Date()

  switch (period) {
    case '7d':
      start.setDate(end.getDate() - 7)
      break
    case '30d':
      start.setDate(end.getDate() - 30)
      break
    case '90d':
      start.setDate(end.getDate() - 90)
      break
    case '1y':
      start.setFullYear(end.getFullYear() - 1)
      break
    default:
      start.setDate(end.getDate() - 30)
  }

  return { start, end }
}

export function useDashboardMetrics(params: DashboardMetricsQuery = { period: '30d' }) {
  return useQuery<DashboardMetrics>({
    queryKey: ['super-admin', 'dashboard', 'metrics', params],
    queryFn: async () => {
      const { start, end } = params.start_date && params.end_date
        ? { start: new Date(params.start_date), end: new Date(params.end_date) }
        : getDateRange(params.period || '30d')

      // Get total organizations
      const { count: totalOrgs } = await supabase
        .from('organizations')
        .select('*', { count: 'exact', head: true })

      // Get new organizations in period
      const { count: newOrgs } = await supabase
        .from('organizations')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', start.toISOString())
        .lte('created_at', end.toISOString())

      const { count: suspendedOrganizations } = await supabase
        .from('organizations')
        .select('*', { count: 'exact', head: true })
        .eq('account_status', 'suspended')

      // Get active subscriptions
      const { count: activeSubscriptions } = await supabase
        .from('subscriptions')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'active')

      // Get organizations by status
      const { data: billingSnapshots } = await supabase
        .from('v_organization_billing_snapshot')
        .select('billing_status, current_period_end')

      const statusBreakdown: Record<DashboardBillingStatus, number> = {
        none: 0,
        trial: 0,
        active: 0,
        past_due: 0,
        canceled: 0,
        suspended: 0,
      }
      for (const snapshot of billingSnapshots || []) {
        const status = normalizeDashboardBillingStatus(snapshot.billing_status)
        statusBreakdown[status] = (statusBreakdown[status] || 0) + 1
      }

      // Get trial expiring soon (next 7 days)
      const trialExpireDate = new Date()
      trialExpireDate.setDate(trialExpireDate.getDate() + 7)
      const trialExpiring = (billingSnapshots || []).filter((snapshot) => {
        if (normalizeDashboardBillingStatus(snapshot.billing_status) !== 'trial') return false
        if (!snapshot.current_period_end) return false
        const periodEnd = new Date(snapshot.current_period_end)
        return periodEnd >= new Date() && periodEnd <= trialExpireDate
      }).length

      // Get total MRR from active subscriptions
      const { data: mrrData } = await supabase
        .from('subscriptions')
        .select('subscription_plans(price_monthly_cents)')
        .eq('status', 'active')

      const totalMrr = ((mrrData as SubscriptionRevenueRow[] | null) || []).reduce(
        (sum, sub) => sum + (sub.subscription_plans?.price_monthly_cents || 0),
        0
      )

      // Get alerts count
      const { count: activeAlerts } = await supabase
        .from('alerts')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'active')

      return {
        total_organizations: totalOrgs || 0,
        new_organizations: newOrgs || 0,
        active_subscriptions: activeSubscriptions || 0,
        trial_subscriptions: statusBreakdown['trial'] || 0,
        churned_subscriptions: statusBreakdown['canceled'] || 0,
        suspended_organizations: suspendedOrganizations || 0,
        trial_expiring_soon: trialExpiring,
        total_mrr: totalMrr,
        total_arr: totalMrr * 12,
        active_alerts: activeAlerts || 0,
        period: params.period || '30d',
        // Growth metrics would need historical data comparison
        organizations_growth: 0,
        mrr_growth: 0,
        churn_rate: 0,
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchInterval: 1000 * 60 * 5, // Auto-refresh every 5 minutes
  })
}
